import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Bike,
  BriefcaseBusiness,
  Contrast,
  Droplets,
  HeartHandshake,
  Image as ImageIcon,
  Leaf,
  MapPin,
  MousePointerClick,
  ShoppingBag,
  Sprout,
  Store,
  Sun,
  TrendingUp,
} from "lucide-react";
import ColorThemeSwitcher from "@/components/ColorThemeSwitcher";
import BrandLogo from "@/components/BrandLogo";

const goals = [
  {
    number: "01",
    icon: TrendingUp,
    title: "مواكبة التطور",
    description:
      "نريد أن تكون كفرنبل حاضرة في التحول الرقمي بخدمة سهلة وعملية، تنظّم الطلب والتوصيل وتقرّب المتاجر المحلية من الناس عبر تجربة حديثة وواضحة.",
  },
  {
    number: "02",
    icon: BriefcaseBusiness,
    title: "تأمين فرص عمل",
    description:
      "في وقت يحتاج فيه الناس إلى أبواب دخل مرنة، يفتح البرنامج فرصًا للسائقين، ويساعد أصحاب المتاجر على الوصول إلى زبائن أكثر وتنمية أعمالهم.",
  },
];

const colorStory = [
  {
    name: "التين",
    label: "بنفسجي تيني",
    hex: "#7B284C",
    usage: "الأزرار والروابط والنقاط المهمة",
    description:
      "لون ثمار كفرنبل الحلوة والغنية. اخترناه للعناصر المهمة والدعوات إلى الحركة، ليحمل دفء المدينة وكرمها من دون أن يزاحم الأخضر.",
    color: "hsl(var(--primary))",
    icon: Sprout,
  },
  {
    name: "الزيتون",
    label: "أخضر زيتوني",
    hex: "#404F26",
    usage: "العناوين والهوية والمساحات الراسخة",
    description:
      "مستمد من أشجار الزيتون الراسخة في أرضنا. هو لون الثقة والثبات والانتماء، ويمنح واجهة بيب بيب أساسًا هادئًا ومريحًا.",
    color: "hsl(var(--brand))",
    icon: Leaf,
  },
  {
    name: "زيت الزيتون",
    label: "ذهبي هادئ",
    hex: "#BD9A47",
    usage: "اللمسات الصغيرة والتفاصيل الدافئة",
    description:
      "لمسة دافئة مستوحاة من لمعان زيت الزيتون تحت الضوء. تظهر في الخلفيات الفاتحة والتفاصيل الصغيرة لتمنح الواجهة دفئًا وراحة.",
    color: "hsl(42 47% 51%)",
    icon: Droplets,
  },
  {
    name: "الأرض والضوء",
    label: "كريمي دافئ",
    hex: "#FAF9F5",
    usage: "الخلفيات ومساحات القراءة الهادئة",
    description:
      "خلفية محايدة مستوحاة من الحجر الفاتح وضوء النهار. تمنح الصور والألوان مساحة للتنفس وتحافظ على راحة العين أثناء الاستخدام.",
    color: "hsl(var(--background))",
    icon: Sun,
  },
];

const ecosystem = [
  { icon: ShoppingBag, title: "المشتري", description: "يجد احتياجاته ويطلبها بخطوات بسيطة." },
  { icon: Store, title: "صاحب المتجر", description: "يعرض منتجاته ويصل إلى زبائن أكثر." },
  { icon: Bike, title: "السائق", description: "يحصل على فرص توصيل وعمل مرن." },
];

const identityPrinciples = [
  {
    icon: Contrast,
    title: "الوضوح أولًا",
    description: "فوق الأخضر الزيتوني نستخدم الأبيض أو الكريمي فقط، ليبقى كل نص مقروءًا وواضحًا.",
  },
  {
    icon: MousePointerClick,
    title: "لكل لون وظيفة",
    description: "البنفسجي للحركة، والزيتوني للثقة، والذهبي كلَمسة صغيرة؛ لا تتنافس الألوان على انتباه المستخدم.",
  },
  {
    icon: ImageIcon,
    title: "صور محلية وصادقة",
    description: "نعتمد الضوء الطبيعي والخامات الحقيقية وصور الناس والمتاجر والمنتجات بلا مبالغة أو زينة منفصلة عن المكان.",
  },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95">
        <div className="safe-top mx-auto flex min-h-[64px] max-w-6xl items-center justify-between gap-3 px-3 py-2 sm:h-[72px] sm:gap-6 sm:px-5 sm:py-3">
          <Link to="/" aria-label="بيب بيب - الرئيسية">
            <BrandLogo className="h-12 w-[138px] object-contain" />
          </Link>
          <nav className="hidden flex-1 items-center justify-center gap-6 text-xs font-extrabold text-muted-foreground md:flex">
            <a href="#story" className="transition-colors hover:text-primary">قصة التطبيق</a>
            <a href="#colors" className="transition-colors hover:text-primary">قصة الألوان</a>
            <a href="#identity" className="transition-colors hover:text-primary">الهوية البصرية</a>
          </nav>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex min-h-10 items-center gap-1.5 rounded-full bg-secondary px-3 py-2 text-xs font-extrabold text-secondary-foreground transition-colors hover:bg-secondary/75 sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm"
            >
              <ArrowRight className="h-4 w-4" />
              رجوع
            </button>
            <ColorThemeSwitcher />
          </div>
        </div>
      </header>

      <main>
        <section className="px-4 pt-5 sm:px-5 sm:pt-8">
          <div className="mx-auto grid max-w-6xl overflow-hidden rounded-[32px] bg-brand shadow-brand lg:grid-cols-[0.88fr_1.12fr]">
            <div className="brand-panel relative flex min-h-[300px] items-center overflow-hidden px-5 py-8 text-white sm:min-h-[430px] sm:px-10 sm:py-12 lg:px-12 lg:py-16">
              <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full border border-white/10" />
              <div className="absolute -bottom-28 -left-10 h-64 w-64 rounded-full bg-white/[0.045]" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold text-white ring-1 ring-white/15">
                  <MapPin className="h-4 w-4" />
                  من كفرنبل، لأهل كفرنبل وريفها
                </div>
                <h1 className="mt-5 text-3xl font-extrabold leading-[1.3] tracking-[-0.03em] sm:mt-7 sm:text-5xl lg:text-[52px]">
                  قصة تطبيق،
                  <span className="mt-1 block text-brand-foreground">وهوية من أرضنا.</span>
                </h1>
                <p className="mt-6 max-w-xl text-sm leading-7 text-white/75 sm:text-base sm:leading-8">
                  بيب بيب فكرة محلية تستخدم التقنية لتسهيل حياة الناس، دعم متاجر كفرنبل، وصناعة فرص عمل جديدة.
                </p>
              </div>
            </div>

            <figure className="relative min-h-[220px] overflow-hidden sm:min-h-[310px] lg:min-h-[540px]">
              <img
                src="/images/brand/kaforder-fig-olive-identity.jpg"
                alt="تين وزيتون وزيت زيتون من كفرنبل"
                width="1696"
                height="960"
                loading="eager"
                className="h-full w-full object-cover"
              />
              <figcaption className="absolute bottom-5 left-5 rounded-full bg-card/90 px-4 py-2 text-xs font-extrabold text-foreground shadow-card">
                ألوان مأخوذة من تفاصيل كفرنبل
              </figcaption>
            </figure>
          </div>
        </section>

        <section id="story" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-12 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <span className="text-xs font-extrabold text-primary">قصة التطبيق</span>
            <h2 className="mt-2 text-3xl font-extrabold leading-snug sm:text-4xl">فكرة بدأت من حاجة محلية</h2>
            <p className="mt-4 text-sm leading-7 text-muted-foreground sm:text-base">
              لم نبدأ من فكرة تطبيق فقط، بل من سؤال يخص حياتنا اليومية: كيف نجعل طلب الاحتياجات أسهل، ونمنح المتاجر المحلية طريقة حديثة للوصول إلى الناس، ونخلق باب عمل جديد للشباب؟
            </p>
          </div>

          <div className="mt-10 grid items-stretch gap-7 lg:grid-cols-[1.12fr_.88fr]">
            <figure className="relative min-h-[320px] overflow-hidden rounded-[30px] shadow-card sm:min-h-[440px]">
              <img
                src="/images/brand/kaforder-app-story.jpg"
                alt="صاحب متجر ومشترية وسائق توصيل يجتمعون في تجربة بيب بيب"
                width="1696"
                height="960"
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <figcaption className="absolute inset-x-4 bottom-4 rounded-[20px] bg-brand/90 px-5 py-3 text-xs font-bold leading-6 text-white shadow-brand">
                حلقة واحدة تربط الناس بالمتاجر والسائقين، وتبقي الحركة الاقتصادية داخل المدينة.
              </figcaption>
            </figure>

            <div className="flex flex-col rounded-[30px] border border-border bg-card p-6 shadow-card sm:p-8">
              <p className="text-base font-semibold leading-8 text-foreground">
                وُلد بيب بيب ليجمع ثلاثة أطراف في مساحة واحدة: المشتري الذي يريد خدمة مريحة، وصاحب المتجر الذي يريد أن ينمّي عمله، والسائق الذي يبحث عن فرصة دخل مرنة.
              </p>
              <p className="mt-4 text-sm leading-7 text-muted-foreground">
                التقنية هنا ليست غاية بحد ذاتها؛ هي أداة لتنظيم الطلب والتوصيل، توفير الوقت، وتقوية العلاقة بين أهل المدينة وسوقها المحلي. نبدأ من كفرنبل ونكبر بثقة أهلها ومشاركتهم.
              </p>
              <div className="mt-7 grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
                {ecosystem.map((item) => (
                  <div key={item.title} className="flex items-center gap-3 rounded-[18px] bg-secondary/55 p-3.5">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground">
                      <item.icon className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-sm font-extrabold">{item.title}</span>
                      <span className="mt-0.5 block text-[10px] leading-5 text-muted-foreground">{item.description}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-border bg-secondary/45">
          <div className="mx-auto max-w-6xl px-5 py-20 lg:py-24">
            <div className="max-w-2xl">
              <span className="text-xs font-extrabold text-primary">لماذا بدأنا؟</span>
              <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">هدفان واضحان منذ اليوم الأول</h2>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {goals.map((goal) => (
                <article key={goal.number} className="relative overflow-hidden rounded-[28px] border border-border bg-card p-7 shadow-card sm:p-9">
                  <span className="absolute left-6 top-4 font-num text-5xl font-extrabold text-primary/[0.065] sm:text-6xl">{goal.number}</span>
                  <div className="grid h-[52px] w-[52px] place-items-center rounded-2xl bg-primary/10 text-primary">
                    <goal.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-2xl font-extrabold">{goal.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-muted-foreground sm:text-base">{goal.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="colors" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-12 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <span className="text-xs font-extrabold text-primary">قصة الألوان</span>
            <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">ألوان من أرض كفرنبل</h2>
            <p className="mt-4 text-sm leading-7 text-muted-foreground sm:text-base">
              استبدلنا البرتقالي بهوية أقرب إلى مدينتنا. الألوان الجديدة مستوحاة من التين والزيتون وزيت الزيتون، مع قاعدة واضحة: لا نضع لونًا دافئًا فوق الأخضر الداكن؛ النص فوق المساحات الزيتونية يبقى أبيض أو كريميًا ليكون مقروءًا ومريحًا.
            </p>
          </div>

          <div className="mt-8 flex h-4 overflow-hidden rounded-full ring-1 ring-border" aria-label="لوحة ألوان بيب بيب">
            {colorStory.map((item) => <span key={item.name} className="flex-1" style={{ backgroundColor: item.color }} />)}
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {colorStory.map((item) => (
              <article key={item.name} className="rounded-[26px] border border-border bg-card p-6 shadow-card">
                <div className="flex items-center justify-between gap-4">
                  <span className="h-16 w-16 rounded-[22px] shadow-inner ring-4 ring-background" style={{ backgroundColor: item.color }} aria-hidden="true" />
                  <item.icon className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="mt-6 text-xl font-extrabold">{item.name}</h3>
                <div className="mt-1 flex items-center justify-between gap-3 text-xs font-bold text-primary">
                  <span>{item.label}</span>
                  <span dir="ltr" className="font-num text-[10px] text-muted-foreground">{item.hex}</span>
                </div>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.description}</p>
                <div className="mt-5 border-t border-border pt-4">
                  <span className="block text-[9px] font-extrabold text-muted-foreground">يُستخدم في</span>
                  <span className="mt-1 block text-xs font-bold leading-5 text-foreground">{item.usage}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="identity" className="scroll-mt-24 bg-brand text-white">
          <div className="mx-auto grid max-w-6xl gap-8 px-5 py-20 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:py-24">
            <div className="rounded-[30px] bg-white p-7 text-foreground shadow-2xl sm:p-9">
              <div>
                <BrandLogo className="h-20 w-56 object-contain" />
                <h2 className="mt-5 text-2xl font-extrabold">شعار واضح وسهل التذكّر</h2>
                <p className="mt-1 text-xs leading-6 text-muted-foreground">شعار بيب بيب الكامل يجمع الاسم مع رمز الطائر في علامة واحدة.</p>
              </div>
              <div className="mt-7 rounded-[22px] bg-secondary/65 p-5">
                <div className="text-[10px] font-extrabold text-primary">شخصية الهوية</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["محلية", "واضحة", "دافئة", "عملية", "موثوقة"].map((word) => (
                    <span key={word} className="rounded-full bg-card px-3 py-1.5 text-xs font-bold shadow-sm">{word}</span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <span className="text-xs font-extrabold text-white/65">مبادئ الهوية البصرية</span>
              <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">هوية جميلة لأنها واضحة ومرتبطة بالمكان</h2>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-white/70 sm:text-base">
                لا نستخدم الألوان كزينة فقط؛ لكل لون وصورة ومساحة دور يساعد المستخدم على الفهم ويذكّره بأن هذه الخدمة خرجت من بيئته المحلية.
              </p>

              <div className="mt-8 grid gap-4 md:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                {identityPrinciples.map((item) => (
                  <article key={item.title} className="rounded-[24px] bg-white/10 p-5 ring-1 ring-white/10">
                    <span className="grid h-11 w-11 place-items-center rounded-2xl bg-white text-brand">
                      <item.icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 text-lg font-extrabold">{item.title}</h3>
                    <p className="mt-2 text-xs leading-6 text-white/65">{item.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 pb-20 lg:pb-24">
          <div className="grid gap-8 rounded-[32px] bg-accent p-7 sm:p-10 lg:grid-cols-[1fr_auto] lg:items-center lg:p-12">
            <div className="flex items-start gap-4">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground">
                <HeartHandshake className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-extrabold sm:text-3xl">نبنيها معًا</h2>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
                  نجاح بيب بيب يقاس بقدر ما يسهّل حياة الناس، يدعم المتاجر، ويخلق فرصًا حقيقية لأبناء المنطقة.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/auth")}
              className="inline-flex h-[52px] items-center justify-center gap-2 rounded-full bg-primary px-7 font-extrabold text-primary-foreground shadow-cta transition-transform hover:-translate-y-0.5"
            >
              انضم إلى بيب بيب <ArrowLeft className="h-5 w-5" />
            </button>
          </div>
        </section>
      </main>

      <footer className="border-t border-border bg-card py-6 text-center text-sm text-muted-foreground">
        بيب بيب © 2026 — فكرة من كفرنبل
      </footer>
    </div>
  );
}
